import clsx from "clsx";
import type { ComponentProps, ReactNode } from "react";
import { useCallback, useState } from "react";
import { WithChat } from "~/components/common/chat/WithChat";
import { DealLayoutHeaderClient } from "~/components/common/layouts/deals/DealLayoutHeaderClient";
import { ChatContextProvider } from "~/context/ChatContext";
import { useMessages as useClientMessages } from "~/hooks/deals/useMessages";
import useItandiAccount from "~/hooks/useItandiAccount";
import { MEDIA_QUERY, useMediaQuery } from "~/hooks/useMediaQuery";
import type { Deal } from "~/models/Deal";
import { getOriginDealVendor } from "~/models/DealVendorCompany";
import Styles from "./DealLayout.module.scss";

type Props = {
	children: ReactNode;
	deal: Deal;
	enableChat?: boolean;
	items: ComponentProps<typeof DealLayoutHeaderClient>["items"];
	noBackground?: boolean;
	useSimpleSpHeader?: boolean;
};

export function ClientDealLayout({
	deal,
	children,
	items,
	noBackground = false,
	useSimpleSpHeader = false,
	enableChat = false
}: Props) {
	const { data: session } = useItandiAccount().currentAccount();
	const { data: messages } = useClientMessages().index(deal.id);
	const { match: isSingleColumn } = useMediaQuery(MEDIA_QUERY.singleColumn);
	const [isOpen, setIsOpen] = useState(false);
	const isChatOpen = !isSingleColumn && isOpen;
	const isDealOwnManagement =
		getOriginDealVendor(deal)?.companyVendor?.dealManagementTypeOwn ?? false;
	const isChatEnabled = enableChat && !isDealOwnManagement;

	const onToggleChat = useCallback(() => {
		setIsOpen((prev) => !prev);
	}, []);

	if (session === null || messages === null) {
		return null;
	}

	return (
		<div>
			<DealLayoutHeaderClient
				deal={deal}
				enableChat={isChatEnabled}
				isChatOpen={isChatOpen}
				isV2DealDetail={session.company?.useV2DealDetail ?? false}
				items={items}
				messages={messages}
				onToggleChat={onToggleChat}
				useSimpleSpHeader={useSimpleSpHeader}
			/>
			<WithChat
				deal={deal}
				isAdmin={session.isAdmin}
				isOpen={isChatOpen}
				messages={messages}
				onClose={onToggleChat}
				receiver={"maintenance_company"}
			>
				<ChatContextProvider isChatOpen={isChatOpen}>
					<div
						className={clsx(
							Styles.Container,
							noBackground ? Styles.Transparent : Styles.White
						)}
					>
						{children}
					</div>
				</ChatContextProvider>
			</WithChat>
		</div>
	);
}
